import { isNil } from 'lodash-es';

import {
  getSubjectForSeries,
  HISTOGRAM_DEFAULT_VALUE_ACCESSOR,
} from './get-subject-for-series.js';
import type {
  ColorRuleProps,
  MatchValue,
} from '../../../core/types/color-rule.js';
import { getColorPalette } from '../../../core/utils/get-color-palette.js';
import {
  buildValidator,
  VALUE_MATCHERS,
} from '../../../core/utils/validators.js';
import type { HistogramSeries } from '../../../histogram/types/histogram.js';

/**
 * Pre-resolves histogram color rules to XY-compatible color-only rules.
 *
 * The histogram assigns palette colors per-series with an incrementing index
 * (each matching series gets the next color from the palette).
 * The XY chart's bar series always picks index 0 for colorPalette rules,
 * so we resolve palettes here to match histogram behavior.
 *
 * Rules with a direct `color` value are passed through unchanged (with the
 * default valueAccessor applied). Rules with `colorPalette` are expanded into
 * one per-series `equals` rule with the resolved color string.
 *
 * Invalid palette names are silently skipped so that partially-typed values
 * in a CodeEditor do not crash the chart.
 */
export function resolveHistogramColorRules(
  seriesData: HistogramSeries[],
  colorRules: ColorRuleProps[],
): ColorRuleProps[] {
  const isValid = buildValidator(VALUE_MATCHERS);
  const resolved: ColorRuleProps[] = [];

  for (const rule of colorRules) {
    if (!rule.colorPalette) {
      resolved.push({
        ...rule,
        valueAccessor: rule.valueAccessor ?? HISTOGRAM_DEFAULT_VALUE_ACCESSOR,
      });
      continue;
    }

    // Resolve colorPalette rule: iterate series with incrementing index
    // to assign different palette colors to each matching series.
    let paletteIndex = 0;
    for (const series of seriesData) {
      const subject = getSubjectForSeries(rule, series);
      if (
        !isNil(subject) &&
        isValid(subject as MatchValue, rule.comparator, rule.matchValue)
      ) {
        try {
          const colorToken = getColorPalette(
            series.name,
            paletteIndex,
            rule.colorPalette,
          );
          resolved.push({
            comparator: 'equals' as const,
            matchValue: series.name,
            color: colorToken.Default,
            valueAccessor: HISTOGRAM_DEFAULT_VALUE_ACCESSOR,
          });
        } catch {
          // Invalid palette name — skip this rule for this series
        }
        paletteIndex++;
      }
    }
  }

  return resolved;
}
